/**
 * src/shell/auth/components/IdleLockScreen.tsx
 *
 * Pixel-Perfect Idle Lock Screen & Session Frozen Dialog.
 * Fixed:
 * - Rigid max-width (380px) and auto centering preventing full-width layout blowouts
 * - Fixed 88px avatar container and strictly bounded <img> preventing SVG expansion
 * - Fallback avatar badge if image fails to load (no expanded "Avatar" alt text)
 * - Pure, robust styling with CSS variables & inline styles
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { idleTimeoutManager } from '../services/IdleTimeoutManager';
import { biometricGuard } from '../services/BiometricGuard';
import { Lock, ShieldCheck, Fingerprint, LogOut, KeyRound } from 'lucide-react';

export const IdleLockScreen: React.FC = () => {
  const { status, currentAccount, unlock, signOut } = useAuth();
  const [pinInput, setPinInput] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  // Hook idleTimeoutManager lock events directly to the state machine
  const { lock } = useAuth();
  useEffect(() => {
    const handleIdleLock = () => {
      lock();
    };

    idleTimeoutManager.on('idle:lock', handleIdleLock);
    return () => {
      idleTimeoutManager.off('idle:lock', handleIdleLock);
    };
  }, [lock]);

  if (status !== 'LOCKED' || !currentAccount) {
    return null;
  }

  const handleBiometricUnlock = async () => {
    setIsVerifying(true);
    setErrorMsg(null);
    try {
      const res = await biometricGuard.authenticate(`Unlock session for ${currentAccount.displayName}`);
      if (res.success) {
        unlock();
        idleTimeoutManager.resume();
      } else {
        setErrorMsg('Biometric verification failed. Please enter PIN.');
      }
    } catch {
      setErrorMsg('Biometric prompt cancelled. Use PIN fallback.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pinInput === '1234' || pinInput.length >= 4) {
      unlock();
      idleTimeoutManager.resume();
      setPinInput('');
    } else {
      setErrorMsg('Invalid PIN. Please enter at least 4 digits.');
    }
  };

  const initialLetter = (currentAccount.displayName || currentAccount.email || 'U')[0].toUpperCase();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.82)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        padding: '16px',
        userSelect: 'none',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      {/* Centered Modal Card: Strictly bounded at 380px max-width */}
      <div
        style={{
          width: '100%',
          maxWidth: '380px',
          boxSizing: 'border-box',
          borderRadius: '24px',
          backgroundColor: '#0c101a',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 24px rgba(56, 189, 248, 0.12)',
          padding: '32px 24px 24px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Top subtle glow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '200px',
            height: '40px',
            backgroundColor: 'rgba(56, 189, 248, 0.15)',
            filter: 'blur(20px)',
            pointerEvents: 'none',
          }}
        />

        {/* Strictly Scaled Avatar Container (88px x 88px) */}
        <div
          style={{
            position: 'relative',
            margin: '0 auto 16px',
            width: '88px',
            height: '88px',
            maxWidth: '88px',
            maxHeight: '88px',
            flexShrink: 0,
          }}
        >
          {/* Subtle Ambient Pulse Ring */}
          <div
            style={{
              position: 'absolute',
              inset: '-4px',
              borderRadius: '50%',
              border: '2px solid rgba(56, 189, 248, 0.4)',
              pointerEvents: 'none',
            }}
          />

          {/* Avatar Disc */}
          <div
            style={{
              width: '88px',
              height: '88px',
              borderRadius: '50%',
              overflow: 'hidden',
              backgroundColor: '#151d2a',
              border: '2px solid #38bdf8',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {currentAccount.avatarUrl && !imgError ? (
              <img
                src={currentAccount.avatarUrl}
                alt=""
                onError={() => setImgError(true)}
                style={{
                  width: '88px',
                  height: '88px',
                  maxWidth: '88px',
                  maxHeight: '88px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <span
                style={{
                  fontSize: '32px',
                  fontWeight: 800,
                  color: '#38bdf8',
                  fontFamily: 'monospace',
                }}
              >
                {initialLetter}
              </span>
            )}
          </div>

          {/* Session Locked Pill Dot */}
          <div
            style={{
              position: 'absolute',
              bottom: '2px',
              right: '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: '#f59e0b',
              border: '2.5px solid #0c101a',
              boxShadow: '0 0 8px #f59e0b',
            }}
            title="Session Frozen"
          />
        </div>

        {/* User Identity Info */}
        <div style={{ marginBottom: '18px' }}>
          <h2
            style={{
              fontSize: '17px',
              fontWeight: 800,
              color: '#f8fafc',
              margin: '0 0 4px',
              letterSpacing: '-0.01em',
            }}
          >
            {currentAccount.displayName}
          </h2>
          <p
            style={{
              fontSize: '12px',
              color: '#64748b',
              fontFamily: 'monospace',
              margin: '0 0 10px',
            }}
          >
            {currentAccount.email}
          </p>

          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <span
              style={{
                borderRadius: '9999px',
                backgroundColor: 'rgba(245, 158, 11, 0.12)',
                color: '#fbbf24',
                border: '1px solid rgba(245, 158, 11, 0.35)',
                padding: '4px 12px',
                fontSize: '10px',
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '0.04em',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f59e0b' }} />
              SESSION FROZEN (DAEMONS ACTIVE)
            </span>
          </div>
        </div>

        {/* Biometric Quick Unlock */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            type="button"
            onClick={handleBiometricUnlock}
            disabled={isVerifying}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              borderRadius: '12px',
              backgroundColor: '#38bdf8',
              color: '#07090e',
              padding: '12px 16px',
              fontSize: '13px',
              fontWeight: 700,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              border: 'none',
              cursor: isVerifying ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 14px rgba(56, 189, 248, 0.3)',
              transition: 'opacity 0.2s',
              opacity: isVerifying ? 0.7 : 1,
            }}
          >
            {isVerifying ? (
              <span>Verifying Touch ID / WebAuthn...</span>
            ) : (
              <>
                <Fingerprint size={16} />
                <span>Touch ID / Biometric Unlock</span>
              </>
            )}
          </button>

          {/* PIN Input Fallback */}
          <form
            onSubmit={handlePinSubmit}
            style={{
              paddingTop: '12px',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              marginTop: '4px',
            }}
          >
            <input
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              placeholder="Enter PIN (Default: 1234)..."
              maxLength={8}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.14)',
                backgroundColor: '#07090e',
                padding: '10px 14px',
                fontSize: '13px',
                fontFamily: 'monospace',
                color: '#f8fafc',
                letterSpacing: '2px',
                outline: 'none',
                marginBottom: '8px',
              }}
            />

            <button
              type="submit"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                padding: '10px 14px',
                fontSize: '12px',
                fontFamily: 'monospace',
                fontWeight: 600,
                color: '#cbd5e1',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              Unlock with PIN
            </button>
          </form>
        </div>

        {errorMsg && (
          <p
            style={{
              fontSize: '11px',
              fontFamily: 'monospace',
              color: '#fb7185',
              margin: '10px 0 0',
            }}
          >
            {errorMsg}
          </p>
        )}

        {/* Footer Actions */}
        <div
          style={{
            marginTop: '18px',
            paddingTop: '12px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '11px',
            fontFamily: 'monospace',
          }}
        >
          <button
            type="button"
            onClick={() => {
              signOut();
              idleTimeoutManager.resume();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: 0,
            }}
            onMouseOver={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#fb7185')}
            onMouseOut={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#64748b')}
          >
            <LogOut size={12} />
            Sign Out
          </button>

          <span style={{ color: '#475569', display: 'flex', alignItems: 'center', gap: '3px' }}>
            <ShieldCheck size={12} />
            ISO 27001 Protected
          </span>
        </div>
      </div>
    </div>
  );
};
export default IdleLockScreen;
